import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { siteSettingsApi } from '../services/api';

interface SiteSettingsContextType {
  settings: any;
  isLoading: boolean;
  error: any;
  getLocalizedText: (text: { en: string; zh: string }) => string;
}

const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(undefined);

interface SiteSettingsProviderProps {
  children: ReactNode;
}

export const SiteSettingsProvider: React.FC<SiteSettingsProviderProps> = ({ children }) => {
  const { data: settingsData, isLoading, error, refetch } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    // Always consider data stale so we refetch on focus and when notified
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    gcTime: 1000 * 60 * 10,
  });

  const settings = settingsData?.settings || null;

  // Helper function to get localized text based on current language
  const getLocalizedText = (text: { en: string; zh: string }) => {
    const currentLang = localStorage.getItem('language') || 'en';
    return text[currentLang as keyof typeof text] || text.en;
  };

  const value = {
    settings,
    isLoading,
    error,
    getLocalizedText
  };

  // Listen for admin updates and refetch immediately (cross-tab, cross-route)
  useEffect(() => {
    const storageHandler = (e: StorageEvent) => {
      if (e.key === 'site_settings_updated') {
        refetch();
      }
    };
    window.addEventListener('storage', storageHandler);

    let bc: BroadcastChannel | undefined;
    try {
      bc = new BroadcastChannel('site-settings');
      bc.onmessage = (ev) => {
        if (ev?.data === 'updated') {
          refetch();
        }
      };
    } catch (_) {
      // BroadcastChannel may be unsupported; storage event will still work
    }

    return () => {
      window.removeEventListener('storage', storageHandler);
      try { bc && bc.close(); } catch (_) {}
    };
  }, [refetch]);

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (context === undefined) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};
